/**
 * QuizInstructions component
 *
 * Description:
 * Displays quiz instructions and allows navigation to previous califications or to the next view.
 *
 * Features:
 * - Shows instructions and description for the quiz.
 * - Allows toggling between instructions and previous califications.
 * - Provides navigation buttons for ratings and next view.
 * - Uses Material-UI for UI elements.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import PrevCalifications from '../PrevCalifications/PrevCalifications';
import classes from './QuizInstructions.module.css';

const QuizInstructions = props => {
  const { t } = useTranslation(['lesson']);
  // Local state for califications view toggle
  const [calificationsView, setCalificationsView] = useState(false);

  const prevAttemptsCount = Array.isArray(props.preCalifications)
    ? props.preCalifications.length
    : 0;
  const isLimitReached =
    props.attempts > 0 && prevAttemptsCount >= props.attempts;

  /**
   * Handles toggling between instructions and previous califications.
   */
  const chanveViewHandler = () => {
    setCalificationsView(!calificationsView);
  };

  return (
    <>
      {/* Custom prop: 'className' for quiz container styling */}
      <div className={classes.quizContainer}>
        <aside className={classes.instructionsContainer}>
          {!calificationsView ? (
            <>
              <p className={classes.instructions__title}>
                {t('instructions')}:
              </p>
              <p className={classes.instructions__text}>
                {props.description}
              </p>
              {props.attempts > 0 ? (
                <div>
                  <p className={classes.instructions__text}>
                    El cuestionario tiene un total de{' '}
                    {props.attempts} intentos.
                  </p>
                  <p className={classes.instructions__text}>
                    El usuario lleva {prevAttemptsCount} intentos.
                  </p>
                </div>
              ) : (
                ''
              )}

              {props.time > 0 && (
                <p className={classes.instructions__text}>
                  Tienes un tiempo máximo de {props.time} minutos
                  para resolver el cuestionario.
                </p>
              )}
            </>
          ) : (
            <PrevCalifications
              // Custom prop: 'califications' passes previous califications data
              califications={props.preCalifications}
            />
          )}
        </aside>
      </div>
      {/* Custom prop: 'className' for button container styling */}
      <aside className={classes.btnsContainer}>
        {props.preCalifications && (
          <Button
            // Custom prop: 'onClick' for toggling view
            onClick={chanveViewHandler}
            className={classes.nextBtn}
          >
            {!calificationsView ? (
              <>
                {t('view_ratings')} <AssignmentIcon />
              </>
            ) : (
              <>
                <ChevronLeftIcon /> {t('back')}
              </>
            )}
          </Button>
        )}
        {!calificationsView && (
          <Button
            // Custom prop: 'onClick' for next view
            onClick={props.changeView}
            className={classes.nextBtn}
            disabled={isLimitReached}
          >
            {t('next')} <ChevronRightIcon />
          </Button>
        )}
      </aside>
    </>
  );
};

QuizInstructions.propTypes = {
  /**
   * Description text for quiz instructions
   */
  description: PropTypes.string,
  /**
   * Course ID (not used directly here)
   */
  idCourse: PropTypes.number,
  /**
   * Array of previous califications
   */
  preCalifications: PropTypes.array,
  /**
   * Allowed attempt limit for the quiz
   */
  attempts: PropTypes.number,
  /**
   * Function to change the view (e.g., go to next step)
   */
  changeView: PropTypes.func,
  time: PropTypes.number
};

QuizInstructions.defaultProps = {
  description: '',
  idCourse: undefined,
  preCalifications: [],
  attempts: 0,
  changeView: undefined,
  time: 0
};

export default QuizInstructions;
